import { lazy, Suspense, type ComponentType } from "react";
import { Route, Routes } from "react-router-dom";
import { AppErrorBoundary } from "./components/error-boundary";
import { AdminLayout, DashboardLayout, ProtectedRoute, PublicLayout } from "./components/layouts";
import { CategoriesPage, CategoryPage, CreatorProfilePage, HomePage, InstitutionalPage, LocalCreatorsPage, NotFoundPage, PricingPage, RankingPage, SearchPage } from "./pages/public";
import { LoginPage, OnboardingPage, PasswordResetPage, SignupPage, VerifyEmailPage } from "./pages/auth";

const CHUNK_RELOAD_KEY = "score-chunk-reload";

// A deploy rewrites every hashed filename, so a tab opened before it asks for
// chunks that no longer exist and the lazy import rejects — which is why the
// screen went blank on the first navigation after a release while a manual
// refresh worked. Reloading fetches the new index.html and its new filenames.
// The session flag keeps a genuinely broken module from looping forever.
function lazyRoute<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      const loaded = await load();
      if (typeof window !== "undefined") sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return loaded;
    } catch (error) {
      if (typeof window === "undefined" || sessionStorage.getItem(CHUNK_RELOAD_KEY)) throw error;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
      window.location.reload();
      // Never settles: the reload replaces this document.
      return new Promise<{ default: T }>(() => undefined);
    }
  });
}

const DashboardAnalyticsPage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardAnalyticsPage })));
const DashboardFeaturedPage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardFeaturedPage })));
const DashboardLinksPage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardLinksPage })));
const DashboardOverviewPage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardOverviewPage })));
const DashboardProfilePage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardProfilePage })));
const DashboardScorePage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardScorePage })));
const DashboardSettingsPage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardSettingsPage })));
const DashboardSubscriptionPage = lazyRoute(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardSubscriptionPage })));
const AdminApiKeysPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminApiKeysPage })));
const AdminCampaignsPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminCampaignsPage })));
const AdminCategoriesPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminCategoriesPage })));
const AdminJobsPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminJobsPage })));
const AdminOverviewPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminOverviewPage })));
const AdminPaymentsPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminPaymentsPage })));
const AdminPricingPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminPricingPage })));
const AdminProfilesPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminProfilesPage })));
const AdminReportsPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminReportsPage })));
const AdminUsersPage = lazyRoute(() => import("./pages/admin").then((module) => ({ default: module.AdminUsersPage })));

function PrivateRouteFallback() {
  return <div className="grid min-h-[60vh] place-items-center text-muted" role="status">Carregando sua área…</div>;
}

export function App() {
  return (
    <AppErrorBoundary>
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="buscar" element={<SearchPage />} />
        <Route path="ranking/:scope?/:estado?/:cidade?" element={<RankingPage />} />
        <Route path="categorias" element={<CategoriesPage />} />
        <Route path="categoria/:slug" element={<CategoryPage />} />
        <Route path="categorias/:slug" element={<CategoryPage />} />
        <Route path="criador/:slug" element={<CreatorProfilePage />} />
        <Route path="criadores/:categoria/:estado?/:cidade?" element={<LocalCreatorsPage />} />
        <Route path="precos" element={<PricingPage />} />
        <Route path="sobre" element={<InstitutionalPage type="sobre" />} />
        <Route path="termos" element={<InstitutionalPage type="termos" />} />
        <Route path="privacidade" element={<InstitutionalPage type="privacidade" />} />
        <Route path="diretrizes" element={<InstitutionalPage type="diretrizes" />} />
        <Route path="contato" element={<InstitutionalPage type="contato" />} />
        <Route path="como-funciona" element={<InstitutionalPage type="como-funciona" />} />
        <Route path="faq" element={<InstitutionalPage type="faq" />} />
      </Route>

      <Route path="login" element={<LoginPage />} />
      <Route path="entrar" element={<LoginPage />} />
      <Route path="cadastro" element={<SignupPage />} />
      <Route path="verificar-email" element={<VerifyEmailPage />} />
      <Route path="recuperar-senha" element={<PasswordResetPage />} />

      <Route element={<Suspense fallback={<PrivateRouteFallback />}><ProtectedRoute /></Suspense>}>
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardOverviewPage />} />
          <Route path="perfil" element={<DashboardProfilePage />} />
          <Route path="links" element={<DashboardLinksPage />} />
          <Route path="analytics" element={<DashboardAnalyticsPage />} />
          <Route path="score" element={<DashboardScorePage />} />
          <Route path="destaques" element={<DashboardFeaturedPage />} />
          <Route path="assinatura" element={<DashboardSubscriptionPage />} />
          <Route path="configuracoes" element={<DashboardSettingsPage />} />
        </Route>
      </Route>

      <Route element={<Suspense fallback={<PrivateRouteFallback />}><ProtectedRoute admin /></Suspense>}>
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="usuarios" element={<AdminUsersPage />} />
          <Route path="perfis" element={<AdminProfilesPage />} />
          <Route path="denuncias" element={<AdminReportsPage />} />
          <Route path="categorias" element={<AdminCategoriesPage />} />
          <Route path="precos" element={<AdminPricingPage />} />
          <Route path="destaques" element={<AdminCampaignsPage />} />
          <Route path="pagamentos" element={<AdminPaymentsPage />} />
          <Route path="api" element={<AdminApiKeysPage />} />
          <Route path="jobs" element={<AdminJobsPage />} />
        </Route>
      </Route>

      <Route element={<PublicLayout />}>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </AppErrorBoundary>
  );
}
